/**
 * Welle B (Punkt 10) — gemeinsame Datei-Aktionen für alle „Datei hinzufügen"-
 * Einstiege (Meine Dateien · Home · Für mich freigegeben) und für das Öffnen von
 * Typen ohne Editor. Eine Quelle der Wahrheit, damit sich alle drei Listen gleich
 * verhalten. `createFile` nimmt Binär-Bodies; kein Backend-Sonderpfad, der Guard
 * (#7) greift über die normalen Content-Endpoints.
 */

import { ryfeApi } from "./api";
import { downloadBytes } from "./file-export";
import { navigate } from "./router";
import { routeForKind } from "./open-file";
import { buildUploadTree, mapUpload } from "./upload-map";

import type { FileMeta } from "./api";

/** Datei hochladen: Format→Typ über upload-map, Bytes 1:1 auf S3. */
export const uploadFileToFolder = async (
  wsId: string,
  folderId: string,
  file: File,
): Promise<FileMeta> => {
  const { kind, contentType } = mapUpload(file.name, file.type);
  const buf = await file.arrayBuffer();
  return ryfeApi.createFile(wsId, folderId, file.name, kind, buf, contentType);
};

/**
 * AP-7 (P17.2) — rekursiver Ordner-Upload. Legt unter `destFolderId` die
 * Ordnerstruktur der ausgewählten Dateien nach (`webkitRelativePath`) an und
 * lädt jede Datei in ihren Ordner. Reine Verkettung der vorhandenen Endpoints
 * (createFolder + uploadFileToFolder) — kein Backend-Sonderpfad, der Guard
 * greift je Ordner/Datei. Gibt die Zahl angelegter Ordner + hochgeladener
 * Dateien zurück.
 */
export const uploadFolderToFolder = async (
  wsId: string,
  destFolderId: string,
  files: File[],
): Promise<{ folders: number; files: number }> => {
  const tree = buildUploadTree(files);
  // Pfad → Ordner-id; "" ist der gewählte Ziel-Ordner selbst.
  const idByPath = new Map<string, string>([["", destFolderId]]);
  for (const path of tree.folders) {
    const parts = path.split("/");
    const name = parts[parts.length - 1];
    const parentPath = parts.slice(0, -1).join("/");
    const parentId = idByPath.get(parentPath) ?? destFolderId;
    const created = await ryfeApi.createFolder(wsId, parentId, name);
    idByPath.set(path, created.id);
  }
  for (const { file, dirPath } of tree.files) {
    const parentId = idByPath.get(dirPath) ?? destFolderId;
    await uploadFileToFolder(wsId, parentId, file);
  }
  return { folders: tree.folders.length, files: tree.files.length };
};

/** URL-Lesezeichen (kind='url'): der Inhalt ist die Ziel-URL. */
export const createUrlBookmark = (
  wsId: string,
  folderId: string,
  name: string,
  url: string,
): Promise<FileMeta> =>
  ryfeApi.createFile(wsId, folderId, name, "url", url, "text/uri-list");

/**
 * Eine Datei öffnen, die keinen Editor hat: `url` → externes Ziel im neuen Tab,
 * `image` → Blob im neuen Tab (ansehen), alles andere (opaque `file`) → Download
 * unter dem (bereinigten) Dateinamen. Editor-Typen (board/md/list) navigieren
 * über `routeForKind`.
 */
export const openStoredFile = async (
  wsId: string,
  file: FileMeta,
): Promise<void> => {
  const route = routeForKind(file.kind, file.id);
  if (route) {
    navigate(route);
    return;
  }
  const buf = await ryfeApi.getFileContent(wsId, file.id);
  const bytes = new Uint8Array(buf);
  if (file.kind === "url") {
    const url = new TextDecoder().decode(bytes).trim();
    if (/^https?:\/\//i.test(url)) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
    return;
  }
  const meta = await ryfeApi.getFile(wsId, file.id).catch(() => null);
  const mime =
    (meta as { content_type?: string } | null)?.content_type ||
    (file.kind === "image" ? "image/*" : "application/octet-stream");
  if (file.kind === "image") {
    const blobUrl = URL.createObjectURL(
      new Blob([bytes.buffer as ArrayBuffer], { type: mime }),
    );
    window.open(blobUrl, "_blank", "noopener,noreferrer");
    return;
  }
  const dlName = file.name.replace(/[\\/:*?"<>|]+/g, "_").trim() || "download";
  downloadBytes(dlName, mime, bytes);
};
